import React, { useMemo, useEffect } from "react";
import Icon from "react-web-vector-icons";
import { getAceLabel, formatFieldType, getFieldHint } from "../../utils/fieldHelpers";
import { getCompatibleSourceFields } from "../../../../../utils/mappingMockData";
import FieldDropdown from "../common/FieldDropdown";
import DefaultValueDropdown from "../common/DefaultValueDropdown";

// List of target fields that should have disabled dropdown in CRM Field column
const DISABLED_CRM_FIELD_MAPPINGS = [
  "Solutions_offered__c",
  "AWS_products__c"
];

// List of target fields that should support multi-select in Default Values column
const MULTI_SELECT_DEFAULT_VALUES = [
  "Primary_Needs_From_Aws__c",
  "AWS_products__c",
  "Sales_Activities__c",
  "Project_Delivery_Models__c",
  "Marketing_Use_Cases__c",
  "Marketing_Channels__c",
  "Solutions_offered__c"
];

const DIGIT_ONLY_FIELDS = [
  "Owner_Phone__c",
  "Partner_Primary_Phone__c",
  "End_Customer_Duns_Number__c",
  "Amount__c"
];

const FieldMappingRow = ({ mapping, onMappingChange, onDefaultValueChange, onDefaultValueChangeWithAutoSave, onMapValues, sourceFields = [], solutions = [] }) => {
  const parseValue = (val) => {
    if (!val) return { apiName: "", objectLabel: "" };
    const parts = String(val).split(".");
    if (parts.length > 1) {
      return { objectLabel: parts[0].toLowerCase(), apiName: parts.slice(1).join(".") };
    }
    return { apiName: val, objectLabel: "" };
  };

  const selectedOption = useMemo(() => {
    const { apiName, objectLabel } = parseValue(mapping.sourceFieldApiName);
    return sourceFields.find((opt) =>
      opt.apiName === apiName &&
      (!objectLabel || opt.objectLabel === objectLabel)
    );
  }, [sourceFields, mapping.sourceFieldApiName]);

  const hasValidMapping = useMemo(() => {
    // Must have a selected option that exists in source fields
    if (!selectedOption) return false;
    const sourceField = mapping.sourceFieldApiName;
    if (!sourceField) return false;
    const trimmed = String(sourceField).trim();
    return trimmed !== "" && trimmed.toLowerCase() !== "none";
  }, [selectedOption, mapping.sourceFieldApiName]);

  // Filter defaultValue for Solutions_offered__c to only include values in solutions list
  const filteredDefaultValue = useMemo(() => {
    if (mapping.targetField.apiName !== "Solutions_offered__c" || solutions.length === 0) {
      return mapping.defaultValue || "";
    }
    
    const defaultValue = mapping.defaultValue || "";
    if (!defaultValue) return "";
    
    const defaultValueArray = defaultValue.split(';').filter(v => v.trim());
    const validSolutionValues = solutions.map(s => s.value);
    const filteredValues = defaultValueArray.filter(v => validSolutionValues.includes(v));
    
    return filteredValues.join(';');
  }, [mapping.targetField.apiName, mapping.defaultValue, solutions]);

  // Auto-update if there are invalid values
  useEffect(() => {
    if (mapping.targetField.apiName === "Solutions_offered__c" && solutions.length > 0) {
      const defaultValue = mapping.defaultValue || "";
      if (defaultValue && filteredDefaultValue !== defaultValue) {
        // Only update if filtered value is different (meaning there were invalid values)
        onDefaultValueChange(mapping.targetField.apiName, filteredDefaultValue);
      }
    }
  }, [mapping.targetField.apiName, mapping.defaultValue, filteredDefaultValue, solutions, onDefaultValueChange]);

  const handleMapValuesClick = () => {
    if (onMapValues) {
      onMapValues(mapping.targetField.apiName);
    }
  };

  // Determine button text based on mapping state (following ACEMapping.page logic)
  const getButtonText = () => {
    const isPicklist = mapping.targetField.type === "PICKLIST" ||
      mapping.targetField.type === "MULTIPICKLIST";

    if (!isPicklist) {
      return null; // Don't show button for non-picklist fields
    }

    // For picklist fields
    const isNone = !mapping.sourceFieldApiName ||
      mapping.sourceFieldApiName === "" ||
      mapping.sourceFieldApiName === "None";

    return isNone ? null : "Map values";
  };

  const buttonText = getButtonText();
  const shouldShowButton = buttonText !== null;

  const hasHint = !!getFieldHint(mapping.targetField.apiName);

  return (
    <tr className={`mapping-table-row ${hasHint ? 'has-hint-row' : ''}`}>
      <td className="mapping-cell">
        <span className="mapping-cell-text">
          {getAceLabel(mapping.targetField.label, mapping.targetField.apiName)}
          {mapping.targetField.required && (
            <span style={{ color: 'red', marginLeft: '4px' }}>*</span>
          )}
          <span style={{ color: '#706e6b', fontSize: '0.9em', marginLeft: '4px' }}>
            ({formatFieldType(mapping.targetField.type)})
          </span>
        </span>
      </td>
      <td className="mapping-cell">
        <span className="mapping-cell-text">
          {mapping.targetField.label}
          <span style={{ color: '#706e6b', fontSize: '0.9em', marginLeft: '4px' }}>
            ({formatFieldType(mapping.targetField.type)})
          </span>
        </span>
      </td>
      <td className="mapping-cell">
        <div className={`opportunity-field-select-container ${getFieldHint(mapping.targetField.apiName) ? 'has-hint' : ''}`}>
          <FieldDropdown
            value={mapping.sourceFieldApiName || ""}
            onChange={(value) =>
              onMappingChange(
                mapping.targetField.apiName,
                value
              )
            }
            compatibleFields={getCompatibleSourceFields(
              mapping.targetField.type,
              sourceFields
            )}
            disabled={DISABLED_CRM_FIELD_MAPPINGS.includes(mapping.targetField.apiName)}
          />
        </div>
        {getFieldHint(mapping.targetField.apiName) && (
          <div className="field-hint">
            {mapping.targetField.apiName === "Amount__c" ? (
              <a
                href="https://calculator.aws"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0176d3', textDecoration: 'none' }}
              >
                {getFieldHint(mapping.targetField.apiName)}
              </a>
            ) : (
              getFieldHint(mapping.targetField.apiName)
            )}
          </div>
        )}
      </td>
      <td className="mapping-cell mapped-cell">
        {hasValidMapping && (
          <Icon
            name="check"
            font="AntDesign"
            color="#40A0C1"
            size={26}
          />
        )}
      </td>
      <td className="mapping-cell">
        {shouldShowButton && (
          <button
            className="map-values-button"
            onClick={handleMapValuesClick}
          >
            {buttonText}
          </button>
        )}
      </td>
      <td className="mapping-cell default-value-cell">
        <div className="field-name-input-container">
          {mapping.targetField.apiName === "Solutions_offered__c" && solutions.length > 0 ? (
            <DefaultValueDropdown
              value={filteredDefaultValue}
              onChange={(value) => {
                // Multi-select dropdowns don't trigger auto-save
                onDefaultValueChange(mapping.targetField.apiName, value);
              }}
              options={solutions}
              multiple={true}
            />
          ) : mapping.targetField.picklistValues && mapping.targetField.picklistValues.length > 0 ? (
            <DefaultValueDropdown
              value={mapping.defaultValue || ""}
              onChange={(value) => {
                // Multi-select dropdowns don't trigger auto-save
                const isMultiSelect = MULTI_SELECT_DEFAULT_VALUES.includes(mapping.targetField.apiName);
                if (isMultiSelect) {
                  onDefaultValueChange(mapping.targetField.apiName, value);
                } else {
                  onDefaultValueChangeWithAutoSave(mapping.targetField.apiName, value);
                }
              }}
              options={mapping.targetField.picklistValues.map(pv => ({
                value: pv.value,
                label: pv.label
              }))}
              multiple={MULTI_SELECT_DEFAULT_VALUES.includes(mapping.targetField.apiName)}
            />
          ) : (
            <input
              type={mapping.targetField.type === "DATE" || mapping.targetField.type === "DATETIME" ? "date" : "text"}
              className="field-name-input"
              value={mapping.defaultValue || ""}
              onChange={(e) => {
                let value = e.target.value;

                const isDigitOnly =
                  DIGIT_ONLY_FIELDS.includes(mapping.targetField.apiName);
                if (isDigitOnly) {
                  value = value.replace(/\D/g, "");
                }

                onDefaultValueChange(
                  mapping.targetField.apiName,
                  value
                )
              }}
              placeholder={mapping.targetField.type === "DATE" || mapping.targetField.type === "DATETIME" ? "mm/dd/yyyy" : "Enter value"}
            />
          )}
        </div>
      </td>
    </tr>
  );
};

export default FieldMappingRow;
